import React, { useState, useEffect } from 'react';
import { Message, User as UserType } from '../types';
import { User as UserIcon, FileText, Quote, ChevronDown, ChevronUp, Copy, Check } from 'lucide-react';

interface ChatMessageProps {
  message: Message;
  user: UserType | null;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message, user }) => {
  const isUser = message.type === 'user';
  const [expandedSources, setExpandedSources] = useState<Set<number>>(new Set());
  const [copied, setCopied] = useState(false);
  
  // Create a unique message ID for this specific message
  const messageId = `message-${message.timestamp.getTime()}-${Math.random().toString(36).substr(2, 9)}`;

  


  // Handle citation clicks for this specific message
  useEffect(() => {
    const handleCitationClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.classList.contains('citation-link') && target.getAttribute('data-message-id') === messageId) {
        const sourceIndex = target.getAttribute('data-source');
        if (sourceIndex !== null) {
          const index = parseInt(sourceIndex);
          // Scroll to the source within this message
          const sourceElement = document.querySelector(`[data-message-id="${messageId}"][data-source-index="${index}"]`);
          if (sourceElement) {
            sourceElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Highlight the source briefly
            sourceElement.classList.add('highlight-source');
            setTimeout(() => {
              sourceElement.classList.remove('highlight-source');
            }, 2000);
          }
        }
      }
    };

    // Handle form download link clicks to prevent React Router interception
    const handleFormLinkClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if clicked element is a link or inside a link
      const link = target.closest('a') as HTMLAnchorElement;
      if (link) {
        // Check if it's a form download link (has class or URL contains /api/forms/download)
        const isFormLink = link.classList.contains('form-download-link') || 
                          link.href.includes('/api/forms/download') ||
                          link.getAttribute('data-url')?.includes('/api/forms/download');
        
        if (isFormLink) {
          event.preventDefault();
          event.stopPropagation();
          const url = link.getAttribute('data-url') || link.href;
          if (url) {
            // Open in new window/tab, bypassing React Router
            window.open(url, '_blank', 'noopener,noreferrer');
          }
        }
      }
    };

    document.addEventListener('click', handleCitationClick);
    document.addEventListener('click', handleFormLinkClick);
    return () => {
      document.removeEventListener('click', handleCitationClick);
      document.removeEventListener('click', handleFormLinkClick);
    };
  }, [messageId]);

  // Function to toggle source expansion
  const toggleSource = (index: number) => {
    const newExpanded = new Set(expandedSources);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedSources(newExpanded);
  };

  // Function to parse and format source metadata
  const formatSourceInfo = (sourceStr: string) => {
    try {
      // Try to parse as JSON/dict string
      const cleanStr = sourceStr.replace(/'/g, '"');
      const sourceData = JSON.parse(cleanStr);
      
      // Try different date field names with more variations
      let dateValue = null;
      const dateFields = [
        'creationdate', 'creation_date', 'date', 'created', 'timestamp', 'created_at',
        'modification_date', 'modified', 'last_modified', 'last_updated', 'updated_at',
        'publish_date', 'published', 'document_date', 'file_date', 'scan_date'
      ];
      
      for (const field of dateFields) {
        if (sourceData[field]) {
          dateValue = sourceData[field];
          break;
        }
      }
      
      // Also check nested objects
      if (!dateValue && sourceData.metadata) {
        for (const field of dateFields) {
          if (sourceData.metadata[field]) {
            dateValue = sourceData.metadata[field];
            break;
          }
        }
      }
      
      // Format the date with better error handling
      let formattedDate = 'Unknown Date';
      if (dateValue) {
        try {
          // Handle different date formats
          let date;
          if (typeof dateValue === 'string') {
            // Try parsing as ISO string, timestamp, or various formats
            if (dateValue.includes('T') || dateValue.includes('-')) {
              date = new Date(dateValue);
            } else if (/^\d{10,13}$/.test(dateValue)) {
              // Unix timestamp (seconds or milliseconds)
              const timestamp = parseInt(dateValue);
              date = new Date(timestamp > 1000000000000 ? timestamp : timestamp * 1000);
            } else {
              date = new Date(dateValue);
            }
          } else if (typeof dateValue === 'number') {
            // Handle numeric timestamps
            date = new Date(dateValue > 1000000000000 ? dateValue : dateValue * 1000);
          } else {
            date = new Date(dateValue);
          }
          
          if (!isNaN(date.getTime())) {
            formattedDate = date.toLocaleDateString();
          } else {
            console.log('Invalid date value:', dateValue);
          }
        } catch (dateError) {
          console.log('Date parsing error:', dateError, 'Date value:', dateValue);
        }
      } else {
        console.log('No date field found in source data. Available fields:', Object.keys(sourceData));
      }
      
      // Get filename - prioritize company_tag and file_name, NEVER show temp paths
      let displayFileName = 'Unknown Document';
      if (sourceData.company_tag && !sourceData.company_tag.startsWith('tmp')) {
        displayFileName = sourceData.company_tag;
      } else if (sourceData.file_name && !sourceData.file_name.startsWith('tmp')) {
        displayFileName = sourceData.file_name;
      }
      // If source path contains actual filename (not temp), extract it
      else if (sourceData.source && !sourceData.source.includes('/tmp/')) {
        const sourcePath = sourceData.source.split('/').pop() || '';
        if (sourcePath && !sourcePath.startsWith('tmp')) {
          displayFileName = sourcePath;
        }
      }
      
      // Handle page numbers - support both single page and multiple pages (comma-separated)
      let pageDisplay = sourceData.page || sourceData.page_label || 'Unknown Page';
      
      // If pages_list exists, format it nicely (e.g., "Pages 1, 3, 5")
      if (sourceData.pages_list && Array.isArray(sourceData.pages_list) && sourceData.pages_list.length > 1) {
        pageDisplay = `Pages ${sourceData.pages_list.join(', ')}`;
      } else if (sourceData.pages_list && Array.isArray(sourceData.pages_list) && sourceData.pages_list.length === 1) {
        pageDisplay = `Page ${sourceData.pages_list[0]}`;
      } else if (pageDisplay && pageDisplay.includes(',')) {
        // If it's already a comma-separated string, format it nicely
        const pages = pageDisplay.split(',').map((p: string) => p.trim());
        if (pages.length > 1) {
          pageDisplay = `Pages ${pages.join(', ')}`;
        }
      }
      
      return {
        fileName: displayFileName,
        page: pageDisplay,
        totalPages: sourceData.total_pages || 'Unknown',
        company: sourceData.company_tag && !sourceData.company_tag.startsWith('tmp') ? sourceData.company_tag : 'Unknown Company',
        date: formattedDate
      };
    } catch (error) {
      console.log('Source parsing error:', error, 'Source string:', sourceStr);
      // Fallback for non-JSON strings
      return {
        fileName: sourceStr.includes('file_name') ? 'Document' : sourceStr,
        page: 'Unknown Page',
        totalPages: 'Unknown',
        company: 'Unknown Company',
        date: 'Unknown Date'
      };
    }
  };

  // Function to format the content without quotes
  const formatContent = (content: string) => {
    // Remove all quote formatting
    let cleanedContent = content
      // Remove quote blocks
      .replace(/```quote\n[\s\S]*?\n```/g, '')
      // Remove raw quote blocks that aren't properly formatted
      .replace(/`quote\n([\s\S]*?)\n`/g, '')
      // Remove standalone backticks that might be leftover
      .replace(/`\s*quote\s*`/g, '')
      // Clean up any remaining raw quote markers
      .replace(/`\s*\[.*?\]quote\s*`/g, '')
      // Remove broken quote formatting
      .replace(/.*`\s*\[\d+\]quote.*/g, '')
      // Remove lines that are just quote markers
      .replace(/^`quote.*$/gm, '')
      .replace(/^`.*$/gm, '');
    
    // Add citations
    const contentWithCitations = addInlineCitations(cleanedContent);
    
    // Make citations clickable
    const contentWithClickableRefs = formatContentWithClickableRefs(contentWithCitations);
    
    // Handle regular text with basic formatting and inline citations
    return (
      <div className="whitespace-pre-wrap">
        {contentWithClickableRefs.split('\n').map((line, lineIndex) => {
          // Handle numbered lists
          if (/^\d+\.\s/.test(line)) {
            return (
              <div key={lineIndex} className="flex items-start gap-3 mb-2">
                <span className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-sm font-medium">
                  {line.match(/^\d+\./)?.[0].replace('.', '')}
                </span>
                <span className="pt-0.5" dangerouslySetInnerHTML={{ __html: formatMarkdownText(line.replace(/^\d+\.\s/, '')) }} />
              </div>
            );
          }
          
          // Handle bullet points
          if (line.startsWith('• ')) {
            return (
              <div key={lineIndex} className="flex items-start gap-3 mb-2">
                <span className="flex-shrink-0 w-2 h-2 bg-gray-400 rounded-full mt-2"></span>
                <span className="pt-0.5" dangerouslySetInnerHTML={{ __html: formatMarkdownText(line.replace(/^•\s/, '')) }} />
              </div>
            );
          }
          
          // Handle empty lines
          if (line.trim() === '') {
            return <div key={lineIndex} className="mb-2"></div>;
          }
          
          return <div key={lineIndex} className="mb-2 leading-relaxed" dangerouslySetInnerHTML={{ __html: formatMarkdownText(line) }} />;
        })}
      </div>
    );
  };

  // Function to format markdown text (bold, italic, links, etc.)
  const formatMarkdownText = (text: string) => {
    // Handle markdown links [text](url) - must be done BEFORE other formatting
    // Also handle links inside bold tags: **[text](url)**
    let formattedText = text.replace(/\*\*\[([^\]]+)\]\(([^)]+)\)\*\*/g, (match, linkText, url) => {
      // This is a link inside bold tags - process as form download link
      const isAbsolute = url.startsWith('http://') || url.startsWith('https://');
      const href = isAbsolute ? url : url;
      const linkId = `form-link-${Math.random().toString(36).substr(2, 9)}`;
      
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline font-medium form-download-link" data-url="${href}" id="${linkId}"><strong>${linkText}</strong></a>`;
    });
    
    // Handle regular markdown links [text](url) - must be done AFTER bold links
    formattedText = formattedText.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, linkText, url) => {
      // Check if URL is absolute (starts with http:// or https://)
      const isAbsolute = url.startsWith('http://') || url.startsWith('https://');
      const href = isAbsolute ? url : url;
      
      // For form download links, use onClick to prevent React Router interception
      // Generate a unique ID for this link
      const linkId = `form-link-${Math.random().toString(36).substr(2, 9)}`;
      
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline font-medium form-download-link" data-url="${href}" id="${linkId}">${linkText}</a>`;
    });
    
    // Handle bold text (**text**) - but skip if it's already been processed as a link
    formattedText = formattedText.replace(/\*\*(.*?)\*\*/g, (match, content) => {
      // Skip if this content is already inside a link tag
      if (content.includes('<a')) {
        return match; // Don't process, already handled
      }
      return `<strong class="text-gray-900 font-semibold">${content}</strong>`;
    });
    
    // Handle italic text (*text*) - but not if it's part of a link
    formattedText = formattedText.replace(/(?<!<a[^>]*>)\*([^*]+?)\*(?!<\/a>)/g, '<em class="text-gray-700 italic">$1</em>');
    
    // Handle code blocks (`text`)
    formattedText = formattedText.replace(/`(.*?)`/g, '<code class="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">$1</code>');
    
    // Handle "Additional Sources Consulted" section with better styling
    formattedText = formattedText.replace(
      /\*\*Additional Sources Consulted\*\*: (.*?)(?=\n|$)/g,
      '<div class="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200"><div class="text-sm text-gray-600 font-medium">Additional Sources Consulted:</div><div class="text-sm text-gray-700 mt-1">$1</div></div>'
    );
    
    return formattedText;
  };

  // Function to add inline source citations
  const addInlineCitations = (content: string) => {
    if (!message.sources || message.sources.length === 0) {
      return content;
    }

    let enhancedContent = content;
    const usedSources = new Set<number>();
    
    // Add source numbers after quotes (only if not already cited)
    message.sources.forEach((source, index) => {
      const citation = ` [${index + 1}]`;
      
      // Add citations after quotes that don't already have citations
      const quoteRegex = /```quote\n([\s\S]*?)\n```(?!\s*\[\d+\])/g;
      if (quoteRegex.test(enhancedContent)) {
        enhancedContent = enhancedContent.replace(quoteRegex, `\`\`\`quote\n$1\n\`\`\`${citation}`);
        usedSources.add(index);
      }
    });

    // Add source citations to paragraphs (preserving line breaks)
    // Split by double line breaks to identify paragraphs
    const paragraphs = enhancedContent.split(/\n\s*\n/);
    const enhancedParagraphs = paragraphs.map((paragraph) => {
      // Skip if it's already a quote block
      if (paragraph.includes('```quote')) {
        return paragraph;
      }
      
      // Skip if it's just whitespace
      if (paragraph.trim() === '') {
        return paragraph;
      }
      
      // Skip if paragraph already has a citation
      if (/\s\[\d+\]\s*$/.test(paragraph)) {
        return paragraph;
      }
      
      // Find the most relevant source for this paragraph
      const bestSourceIndex = findBestSourceForContent(paragraph, message.sources);
      
      if (bestSourceIndex !== -1) {
        // Add citation to the end of the paragraph
        usedSources.add(bestSourceIndex);
        return paragraph + ` [${bestSourceIndex + 1}]`;
      }
      
      return paragraph;
    });
    
    // Don't add additional sources summary since AI now handles it
    enhancedContent = enhancedParagraphs.join('\n\n');
    
    return enhancedContent;
  };

  // Function to format content with clickable references
  const formatContentWithClickableRefs = (content: string) => {
    // Replace citation numbers with clickable links
    const formattedContent = content.replace(/\[(\d+)\]/g, (match, number) => {
      const sourceIndex = parseInt(number) - 1;
      if (sourceIndex >= 0 && message.sources && message.sources[sourceIndex]) {
        return `<span class="citation-link" data-source="${sourceIndex}" data-message-id="${messageId}" style="color: #60A5FA; cursor: pointer; text-decoration: underline;">[${number}]</span>`;
      }
      return match;
    });
    
    return formattedContent;
  };

  // Function to find the best source for a given content
  const findBestSourceForContent = (content: string, sources: any[] | undefined) => {
    if (!sources || sources.length === 0) return -1;
    
    // Keywords that suggest procedural content
    const proceduralKeywords = [
      'process', 'procedure', 'department', 'conducts', 'initiated', 'begins', 'ensures',
      'monitoring', 'checks', 'inventory', 'request', 'regular', 'existing', 'new',
      'notify', 'immediately', 'supervisor', 'safety', 'restrict', 'access', 'scene',
      'event', 'area', 'others', 'ensure', 'report', 'incident', 'accident',
      'cheque', 'book', 'finance', 'manager', 'approval', 'bank', 'coordinate',
      'collection', 'storage', 'secure', 'authorized', 'personnel', 'step', 'steps',
      'preparation', 'review', 'submission', 'verification', 'receipt', 'control'
    ];
    
    // Check if content contains procedural keywords
    const hasProceduralContent = proceduralKeywords.some(keyword => 
      content.toLowerCase().includes(keyword.toLowerCase())
    );
    
    // Lower the threshold to be more inclusive
    if (!hasProceduralContent || content.length < 20) {
      return -1;
    }
    
    // Score each source based on content similarity
    const sourceScores = sources.map((source, index) => {
      let score = 0;
      const sourceContent = source.content || '';
      
      // Check for exact phrase matches (higher weight)
      const contentWords = content.toLowerCase().split(/\s+/).filter((word: string) => word.length > 3);
      const sourceWords = sourceContent.toLowerCase().split(/\s+/).filter((word: string) => word.length > 3);
      
      // Count exact word matches
      const commonWords = contentWords.filter((word: string) => sourceWords.includes(word));
      score += commonWords.length * 2;
      
      // Check for keyword matches (even higher weight)
      proceduralKeywords.forEach(keyword => {
        if (content.toLowerCase().includes(keyword) && sourceContent.toLowerCase().includes(keyword)) {
          score += 5;
        }
      });
      
      // Check for exact phrase matches (highest weight)
      const contentPhrases = content.toLowerCase().match(/[a-z]+(?:\s+[a-z]+){2,}/g) || [];
      contentPhrases.forEach(phrase => {
        if (sourceContent.toLowerCase().includes(phrase)) {
          score += 10;
        }
      });
      
      // Lower the threshold for matching
      return { index, score };
    });
    
    // Return the source with the highest score, with lower threshold
    sourceScores.sort((a, b) => b.score - a.score);
    
    // Lower the threshold to be more inclusive
    if (sourceScores[0].score > 1) {
      return sourceScores[0].index;
    }
    
    return -1;
  };

  // Copy message content to clipboard
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };



  // Format timestamp
  const formatTimestamp = (timestamp: Date) => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return timestamp.toLocaleDateString();
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4 sm:mb-6 animate-slide-up`}>
      <div className={`flex max-w-[90%] sm:max-w-4xl ${isUser ? 'flex-row-reverse' : 'flex-row'} items-start gap-3 sm:gap-4`}>
        {/* Avatar */}
        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center shadow-soft ${
          isUser ? 'bg-gradient-to-br from-primary-100 to-primary-200' : 'bg-gradient-to-br from-primary-100 to-primary-200'
        }`}>
          {isUser ? (
            user && user.displayName ? (
              <span className="text-sm font-semibold text-white">
                {user.displayName.split(' ').map(name => name.charAt(0)).join('').toUpperCase()}
              </span>
            ) : (
              <UserIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            )
          ) : (
            <img src="/chat_logo.png" alt="Chat Bot" className="w-6 h-6 object-contain" />
          )}
        </div>

        {/* Message Content */}
        <div className={`flex-1 ${isUser ? 'text-right' : 'text-left'}`}>
          <div className={`inline-block p-4 sm:p-5 rounded-2xl text-sm sm:text-base message-bubble ${
            isUser 
              ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-medium' 
              : 'glass text-secondary-800 shadow-soft'
          }`}>
            {formatContent(message.content)}
          </div>

          {/* Sources */}
          {message.sources && message.sources.length > 0 && (
            <div className="mt-4 sm:mt-6 space-y-3 sm:space-y-4">
              <div className="text-xs sm:text-sm text-secondary-600 font-semibold flex items-center gap-2">
                <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
                Sources ({message.sources.length})
                <span className="text-xs text-secondary-400 ml-2 hidden sm:inline">
                  Click numbers [1], [2], etc. to view sources
                </span>
              </div>
              <div className="space-y-3">
                {message.sources.map((source, index) => {
                  const isExpanded = expandedSources.has(index);
                  // Use metadata directly if available, otherwise parse source string
                  const sourceInfo = (source as any).metadata 
                    ? formatSourceInfo(JSON.stringify((source as any).metadata))
                    : formatSourceInfo(source.source || '{}');
                  
                  return (
                    <div key={index} className="neumorphism overflow-hidden transition-all duration-200 hover:shadow-medium" data-source-index={index} data-message-id={messageId}>
                      {/* Source Header - Clickable */}
                      <button
                          onClick={() => toggleSource(index)}
                          className="w-full p-4 sm:p-5 text-left hover:bg-white/50 transition-all duration-200 flex items-center justify-between min-h-[48px] sm:min-h-[52px] focus-ring"
                        >
                          <div className="flex items-center gap-3">
                            <div className="text-sm sm:text-base text-secondary-700 font-semibold">
                              {sourceInfo.fileName} [{index + 1}]
                            </div>
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 sm:w-6 sm:h-6 text-secondary-500" />
                          ) : (
                            <ChevronDown className="w-5 h-5 sm:w-6 sm:h-6 text-secondary-500" />
                          )}
                        </button>
                      
                      {/* Expandable Content */}
                      {isExpanded && (
                        <div className="px-3 pb-3">
                          <div className="text-xs text-gray-500 bg-white p-3 rounded border max-h-64 overflow-y-auto">
                            <div className="whitespace-pre-wrap leading-relaxed">
                              {source.content}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Message Actions and Timestamp */}
          <div className={`flex items-center justify-between mt-3 ${
            isUser ? 'flex-row-reverse' : 'flex-row'
          }`}>
            <div className="flex items-center gap-2">
              {!isUser && (
                <>
                  <button
                    onClick={copyToClipboard}
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    title="Copy message"
                  >
                    {copied ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>

                </>
              )}
            </div>
            <div className={`text-xs text-gray-400 ${
              isUser ? 'text-right' : 'text-left'
            }`}>
              {formatTimestamp(message.timestamp)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;
